import { useEffect, useState } from "react";
import { api, euro } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Repeat, Plus, PencilSimple, Trash, CaretDown, CaretRight, Play, Check, Buildings, IdentificationCard, EnvelopeSimple, Phone, Globe, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

const inpCls = "h-11 rounded-lg border-slate-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200";
const monthLabel = (m) => {
  if (!m) return "—";
  const [y, mo] = m.split("-");
  return new Date(+y, +mo-1, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
};
const currentMonth = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,"0")}`;
};

function RecurringDialog({ value, categories, onClose, onSaved }) {
  const [d, setD] = useState(value);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setD(x => ({ ...x, [k]: v }));

  const save = async () => {
    if (!d.purpose || !d.amount || !d.day_of_month || !d.start_month) {
      toast.error("Bitte alle Pflichtfelder ausfüllen."); return;
    }
    setBusy(true);
    try {
      const payload = {
        purpose: d.purpose,
        amount: parseFloat(d.amount),
        category: d.category || "Sonstiges",
        day_of_month: parseInt(d.day_of_month, 10),
        start_month: d.start_month,
        end_month: d.end_month || "",
        notes: d.notes || "",
        active: d.active !== false,
        vendor: d.vendor || "",
        contract_no: d.contract_no || "",
        contact_email: d.contact_email || "",
        contact_phone: d.contact_phone || "",
        contact_website: d.contact_website || "",
      };
      if (d.id) await api.put(`/recurring-expenses/${d.id}`, payload);
      else      await api.post("/recurring-expenses", payload);
      toast.success("Vorlage gespeichert");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o)=>{if(!o) onClose();}}>
      <DialogContent className="max-w-md" data-testid="recurring-dialog">
        <DialogHeader><DialogTitle>{d.id ? "Vorlage bearbeiten" : "Neue wiederkehrende Ausgabe"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Zweck *</Label>
            <Input value={d.purpose} onChange={(e)=>set("purpose", e.target.value)} className={`${inpCls} mt-1`} placeholder="z. B. Strom Stadtwerke" data-testid="rec-purpose"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Betrag * (EUR)</Label>
              <Input type="number" step="0.01" min="0" value={d.amount} onChange={(e)=>set("amount", e.target.value)} className={`${inpCls} mt-1`} placeholder="0,00" data-testid="rec-amount"/>
            </div>
            <div>
              <Label className="text-xs">Kategorie *</Label>
              <Input value={d.category} onChange={(e)=>set("category", e.target.value)} list="rec-cat-list" className={`${inpCls} mt-1`} data-testid="rec-category"/>
              <datalist id="rec-cat-list">{(categories || []).map(c => <option key={c} value={c}/>)}</datalist>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Buchungstag *</Label>
              <Input type="number" min="1" max="28" value={d.day_of_month} onChange={(e)=>set("day_of_month", e.target.value)} className={`${inpCls} mt-1`} data-testid="rec-day"/>
              <div className="text-[10px] text-muted-foreground mt-1">Tag 1–28</div>
            </div>
            <div>
              <Label className="text-xs">Ab Monat *</Label>
              <Input type="month" value={d.start_month} onChange={(e)=>set("start_month", e.target.value)} className={`${inpCls} mt-1`} data-testid="rec-start"/>
            </div>
            <div>
              <Label className="text-xs">Bis Monat</Label>
              <Input type="month" value={d.end_month || ""} onChange={(e)=>set("end_month", e.target.value)} className={`${inpCls} mt-1`} data-testid="rec-end"/>
              <div className="text-[10px] text-muted-foreground mt-1">leer = unbefristet</div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Notiz (optional)</Label>
            <Input value={d.notes || ""} onChange={(e)=>set("notes", e.target.value)} className={`${inpCls} mt-1`}/>
          </div>
          <label className="flex items-center gap-2 pt-1 cursor-pointer">
            <input type="checkbox" checked={d.active !== false} onChange={(e)=>set("active", e.target.checked)} className="h-4 w-4" data-testid="rec-active"/>
            <span className="text-sm">Aktiv (wird jeden Monat automatisch verbucht)</span>
          </label>

          <div className="mt-2 pt-3 border-t border-slate-200">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase tracking-widest mb-2">
              <Buildings size={14} weight="duotone"/> Anbieter / Kontakt (optional)
            </div>
            <div className="space-y-2.5">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Firma / Anbieter</Label>
                  <Input value={d.vendor || ""} onChange={(e)=>set("vendor", e.target.value)} className={`${inpCls} mt-1`} placeholder="z. B. Stadtwerke Miltenberg" data-testid="rec-vendor"/>
                </div>
                <div>
                  <Label className="text-xs">Vertrags-/Kunden-Nr.</Label>
                  <Input value={d.contract_no || ""} onChange={(e)=>set("contract_no", e.target.value)} className={`${inpCls} mt-1`} placeholder="z. B. K-2024-778812" data-testid="rec-contract"/>
                </div>
              </div>
              <div>
                <Label className="text-xs">E-Mail</Label>
                <Input type="email" value={d.contact_email || ""} onChange={(e)=>set("contact_email", e.target.value)} className={`${inpCls} mt-1`} placeholder="service@anbieter.de" data-testid="rec-email"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Telefon</Label>
                  <Input value={d.contact_phone || ""} onChange={(e)=>set("contact_phone", e.target.value)} className={`${inpCls} mt-1`} placeholder="+49 …" data-testid="rec-phone"/>
                </div>
                <div>
                  <Label className="text-xs">Internetseite</Label>
                  <Input value={d.contact_website || ""} onChange={(e)=>set("contact_website", e.target.value)} className={`${inpCls} mt-1`} placeholder="https://…" data-testid="rec-website"/>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="rounded-lg" disabled={busy}>Abbrechen</Button>
          <Button onClick={save} disabled={busy} className="rounded-lg bg-primary text-white" data-testid="rec-save">Speichern</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function RecurringExpenses({ categories, onPosted }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/recurring-expenses").then(r => setRows(r.data)).catch(()=>setRows([]));
  useEffect(() => { load(); }, []);

  const openNew = () => setEdit({
    purpose: "", amount: "", category: "", day_of_month: 1,
    start_month: currentMonth(), end_month: "", notes: "", active: true,
    vendor: "", contract_no: "", contact_email: "", contact_phone: "", contact_website: "",
  });
  const openEdit = (r) => setEdit({ ...r });
  const del = async (id) => {
    if (!window.confirm("Diese Vorlage wirklich löschen?")) return;
    try { await api.delete(`/recurring-expenses/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const toggleActive = async (r) => {
    try {
      await api.put(`/recurring-expenses/${r.id}`, {
        purpose: r.purpose, amount: r.amount, category: r.category,
        day_of_month: r.day_of_month, start_month: r.start_month,
        end_month: r.end_month || "", notes: r.notes || "", active: !r.active,
        vendor: r.vendor || "", contract_no: r.contract_no || "",
        contact_email: r.contact_email || "", contact_phone: r.contact_phone || "",
        contact_website: r.contact_website || "",
      });
      load();
    } catch (e) { toast.error("Fehler beim Umschalten"); }
  };
  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/recurring-expenses/run");
      const n = r.data.posted || 0;
      toast.success(n > 0 ? `${n} Ausgabe(n) verbucht` : "Keine fälligen Ausgaben");
      load();
      onPosted && onPosted();
    } catch (e) { toast.error("Fehler"); }
    finally { setBusy(false); }
  };

  const cur = currentMonth();
  const activeCount = rows.filter(r => r.active).length;

  return (
    <div className="msb-card" data-testid="recurring-card">
      <button onClick={()=>setOpen(o=>!o)} className="w-full flex items-center gap-3 text-left" data-testid="rec-toggle">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
          <Repeat size={18} weight="duotone" className="text-indigo-700"/>
        </div>
        <div className="flex-1">
          <h2 className="font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Wiederkehrende Ausgaben</h2>
          <p className="text-xs text-muted-foreground">
            {rows.length} Vorlage{rows.length === 1 ? "" : "n"} · {activeCount} aktiv · werden automatisch monatlich verbucht
          </p>
        </div>
        {open ? <CaretDown size={16} weight="bold"/> : <CaretRight size={16} weight="bold"/>}
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button onClick={openNew} className="rounded-lg bg-primary text-white" data-testid="btn-add-recurring">
              <Plus size={16} weight="bold"/><span className="ml-1.5">Neue Vorlage</span>
            </Button>
            <Button onClick={runNow} variant="outline" className="rounded-lg" disabled={busy} data-testid="btn-run-recurring">
              <Play size={16} weight="bold"/><span className="ml-1.5">Fällige jetzt verbuchen</span>
            </Button>
          </div>

          {rows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-slate-200 rounded-lg">
              Noch keine Vorlagen angelegt. Legen Sie fixe monatliche Kosten (Strom, Versicherung, Miete …) an — sie werden am gewählten Tag automatisch gebucht.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/60">
                  <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                    <th className="text-left p-3">Aktiv</th>
                    <th className="text-left p-3">Zweck</th>
                    <th className="text-left p-3">Kategorie</th>
                    <th className="text-center p-3">Tag</th>
                    <th className="text-left p-3">Zeitraum</th>
                    <th className="text-right p-3">Betrag</th>
                    <th className="text-center p-3">Diesen Monat</th>
                    <th className="text-right p-3">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const posted = r.last_posted_month === cur;
                    return (
                      <tr key={r.id} className="border-t border-border hover:bg-muted/30" data-testid={`rec-row-${r.id}`}>
                        <td className="p-3">
                          <button onClick={()=>toggleActive(r)} className={`w-8 h-5 rounded-full transition ${r.active ? "bg-emerald-500" : "bg-slate-300"}`} title={r.active ? "Aktiv" : "Pausiert"}>
                            <div className={`w-3 h-3 bg-white rounded-full transform transition ${r.active ? "translate-x-4" : "translate-x-1"}`}/>
                          </button>
                        </td>
                        <td className="p-3 font-semibold">
                          {r.purpose}
                          {r.vendor && <div className="text-[11px] font-normal text-slate-500 mt-0.5">bei {r.vendor}{r.contract_no ? ` · Nr. ${r.contract_no}` : ""}</div>}
                          {r.notes && <div className="text-xs text-muted-foreground font-normal">{r.notes}</div>}
                          {(r.contact_email || r.contact_phone || r.contact_website) && (
                            <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px]">
                              {r.contact_email && (
                                <a href={`mailto:${r.contact_email}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 hover:bg-sky-100" title={r.contact_email} data-testid={`rec-contact-email-${r.id}`}>
                                  <EnvelopeSimple size={11} weight="bold"/> Mail
                                </a>
                              )}
                              {r.contact_phone && (
                                <a href={`tel:${r.contact_phone.replace(/\s/g,'')}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100" title={r.contact_phone} data-testid={`rec-contact-phone-${r.id}`}>
                                  <Phone size={11} weight="bold"/> {r.contact_phone}
                                </a>
                              )}
                              {r.contact_website && (
                                <a href={r.contact_website.startsWith("http") ? r.contact_website : `https://${r.contact_website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 hover:bg-violet-100" title={r.contact_website} data-testid={`rec-contact-web-${r.id}`}>
                                  <Globe size={11} weight="bold"/> Web
                                </a>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-xs">
                          <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700">{r.category}</span>
                        </td>
                        <td className="p-3 text-center font-mono">{r.day_of_month}.</td>
                        <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                          {monthLabel(r.start_month)} → {r.end_month ? monthLabel(r.end_month) : <span className="text-emerald-700">∞</span>}
                        </td>
                        <td className="p-3 text-right font-mono font-bold text-red-600 whitespace-nowrap">{euro(r.amount)}</td>
                        <td className="p-3 text-center">
                          {posted ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold">
                              <Check size={12} weight="bold"/> verbucht
                            </span>
                          ) : r.active ? (
                            <span className="text-xs text-amber-700">wartet</span>
                          ) : (
                            <span className="text-xs text-slate-400">pausiert</span>
                          )}
                        </td>
                        <td className="p-3 text-right whitespace-nowrap">
                          <button onClick={()=>openEdit(r)} className="p-1.5 rounded hover:bg-slate-200" title="Bearbeiten" data-testid={`rec-edit-${r.id}`}>
                            <PencilSimple size={14} weight="bold"/>
                          </button>
                          <button onClick={()=>del(r.id)} className="p-1.5 rounded hover:bg-red-100 text-red-600 ml-1" title="Löschen" data-testid={`rec-del-${r.id}`}>
                            <Trash size={14} weight="bold"/>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {edit && <RecurringDialog value={edit} categories={categories} onClose={()=>setEdit(null)} onSaved={()=>{setEdit(null); load(); onPosted && onPosted();}}/>}
    </div>
  );
}
